import _ from "lodash";
import {
    BufferContext,
    EntryBufferType,
    GridBuffer,
    GenericBufferEntry,
    SizeBufferEntry,
    LocationRowBufferEntry,
    PasteSizeData,
} from "constants/sizingView";
import { IRowData, ISize } from "components/shipment-review/types";
import { IWorkingUpdate } from "actions/cart-legacy";
import { getWOUpdateSizeAction } from "containers/legacy-cart/helpers/common";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { AllocationType } from "types/cart";

interface Data<T extends IRowData | IGridItem | PasteSizeData> {
    rows: T[];
    columns: T extends IRowData ? ISize[] : Array<any>;
}

export const getGridBuffer = <T extends IRowData | IGridItem>(
    context: BufferContext,
    { rows, columns }: Data<T>
): GridBuffer => {
    switch (context) {
        case BufferContext.SizesRow: {
            // get only first row because for this type works ony with one row
            const { sizes } = _.head(<IRowData[]>rows);
            return {
                context,
                entries: <SizeBufferEntry[]>_.map(columns, ({ id }) => ({
                    type: EntryBufferType.Size,
                    key: _.toLower(sizes[id].size.size),
                    value: sizes[id].quantity,
                })),
            };
        }
        case BufferContext.Row: {
            return {
                context,
                entries: <LocationRowBufferEntry[]>_.chain(<IGridItem[]>rows)
                    .map((row) => ({
                        type: EntryBufferType.Entry,
                        key: {
                            attributes: row.attributes,
                            productId: row.productId,
                            prebook: row.location ? _.head(row.entries).prebook : undefined, // TODO: remove within NU-50181
                            location: row.location.id,
                        },
                        value: _.chain(row.entries)
                            .filter({ allocated: AllocationType.ByLocation })
                            .map(({ quantity, size, location, shipStart, shipEnd }) => ({
                                quantity,
                                size: size.size,
                                location: location.id,
                                shipStart,
                                shipEnd,
                            }))
                            .value(),
                    }))
                    .filter(({ value }) => !!_.size(value)) // filter out rows without allocated items
                    .value(),
            };
        }
        default: {
            return {
                context,
                entries: <GenericBufferEntry[]>_.flatMap(columns, ({ id }) =>
                    _.map(<IRowData[]>rows, (row) => ({
                        type: EntryBufferType.Generic,
                        key: `${id}--${row.id}`,
                        value: row[id],
                    }))
                ),
            };
        }
    }
};

export const getPasteActions = <T extends IRowData | IGridItem | PasteSizeData>(
    context: BufferContext,
    buffer: GridBuffer,
    { rows, columns }: Data<T>
): IWorkingUpdate[] => {
    const { entries } = buffer;
    if (!_.size(entries) || !_.size(rows)) {
        return;
    }
    switch (context) {
        case BufferContext.Quantity: {
            switch (buffer.context) {
                case BufferContext.SizesRow: {
                    const actions = [];
                    _.forEach(<IRowData[]>rows, (data) => {
                        _.forEach(columns, ({ id }, idx) => {
                            const value = entries[idx]?.value;
                            if (id && entries[idx] && data.sizes[id] && data.sizes[id].quantity !== value) {
                                actions.push(getWOUpdateSizeAction(data.item, data.sizes[id].size, value));
                            }
                        });
                    });
                    return actions;
                }
            }
            break;
        }
        case BufferContext.Sizes: {
            switch (buffer.context) {
                case BufferContext.SizesRow: {
                    const actions = [];
                    const sizesMap = _.groupBy(<PasteSizeData[]>rows, ({ size }) => _.toLower(size.size));
                    _.forEach(<SizeBufferEntry[]>entries, ({ key, value }) => {
                        _.forEach(sizesMap[key], ({ item, quantity, size }) => {
                            if (quantity !== value) {
                                actions.push(getWOUpdateSizeAction(item, size, value));
                            }
                        });
                    });

                    return actions;
                }
            }
            break;
        }
        case BufferContext.Row: {
            switch (buffer.context) {
                case BufferContext.Row: {
                    const actions = [];
                    const uniqLocations = new Set();
                    const destinationRowsMap = {};

                    _.forEach(<IGridItem[]>rows, (row) => {
                        const rowLocation: string = row.location?.id;
                        uniqLocations.add(rowLocation);
                        _.forEach(row.entries, (entry) => {
                            const entryKey = getEntryKey({
                                attributes: row.attributes,
                                location: rowLocation,
                                productId: row.productId,
                                size: entry.size.size,
                                prebook: rowLocation ? entry.prebook : undefined,
                                shipStart: entry.shipStart,
                                shipEnd: entry.shipEnd,
                            });
                            destinationRowsMap[entryKey] = entry;
                        });
                    });
                    _.forEach(<LocationRowBufferEntry[]>entries, ({ key, value }) => {
                        _.forEach(value, (entry) => {
                            _.forEach(Array.from(uniqLocations), (location: string) => {
                                const destinationEntryKey = getEntryKey({
                                    location,
                                    attributes: key.attributes,
                                    productId: key.productId,
                                    prebook: key.prebook,
                                    size: entry.size,
                                    shipStart: entry.shipStart,
                                    shipEnd: entry.shipEnd,
                                });
                                const destinationEntry = destinationRowsMap[destinationEntryKey];

                                if (destinationEntry && destinationEntry.quantity !== entry.quantity) {
                                    actions.push(
                                        getWOUpdateSizeAction(destinationEntry, destinationEntry.size, entry.quantity)
                                    );
                                }
                            });
                        });
                    });
                    return actions;
                }
            }
        }
    }
};

interface IRowKey {
    size: string;
    productId: string;
    prebook: boolean;
    location: string;
    attributes: IGridItem["attributes"];
    shipStart: Date;
    shipEnd: Date;
}
const getEntryKey = ({ attributes, location, productId, size, shipStart, shipEnd }: IRowKey) =>
    JSON.stringify({
        attributes,
        productId,
        location,
        size,
        shipStart,
        shipEnd,
    });
